import type HealthHiringInsured from '@/modules/health/hiring/models/APP/HealthHiringInsured'
import HealthHiringInsuredHealthSituation from '@/modules/health/hiring/models/APP/HealthHiringInsuredHealthSituation'
import HealthHiringInsuredMedicalSituation from '@/modules/health/hiring/models/APP/HealthHiringInsuredMedicalSituation'
import HealthSituationDescription from '@/modules/health/hiring/models/APP/HealthSituationDescription'
import HealthSituationUsage from '@/modules/health/hiring/models/APP/HealthSituationUsage'
import MedicalSituationAdmission from '@/modules/health/hiring/models/APP/MedicalSituationAdmission'

function responseYesNo(value: boolean) {
  return value ? 's' : 'n'
}

function toFirstBlock(insured: HealthHiringInsured) {
  return [
    {
      questionCode: '05',
      dataType: 'DOUBLE',
      responses: [
        {
          value: String(insured.userDetails.weight),
          responseText: '',
          associatedQuestionnaires: null
        }
      ]
    },
    {
      questionCode: '06',
      dataType: 'INTEGER',
      responses: [
        {
          value: String(insured.userDetails.height),
          responseText: '',
          associatedQuestionnaires: null
        }
      ]
    }
  ]
}

function toSecondBlock(insured: HealthHiringInsured) {
  return [
    {
      questionCode: '0101',
      dataType: 'RADIO',
      responses: [
        {
          value: responseYesNo(!!insured.medicalSituation.medical.length),
          responseText: HealthQuestionnaireMapper.stringlifyAdmissions(
            insured.medicalSituation.medical
          ),
          associatedQuestionnaires: null
        }
      ]
    },
    {
      questionCode: '0301',
      dataType: 'RADIO',
      responses: [
        {
          value: responseYesNo(!!insured.medicalSituation.surgical.length),
          responseText: HealthQuestionnaireMapper.stringlifyAdmissions(
            insured.medicalSituation.surgical
          ),
          associatedQuestionnaires: null
        }
      ]
    },
    {
      questionCode: '1401',
      dataType: 'RADIO',
      responses: [
        {
          value: responseYesNo(!!insured.medicalSituation.cancer.length),
          responseText: HealthQuestionnaireMapper.stringlifyAdmissions(
            insured.medicalSituation.cancer
          ),
          associatedQuestionnaires: null
        }
      ]
    },
    {
      questionCode: '0701',
      dataType: 'RADIO',
      responses: [
        {
          value: responseYesNo(!!insured.medicalSituation.symptoms.length),
          responseText: HealthQuestionnaireMapper.stringlifyAdmissions(
            insured.medicalSituation.symptoms
          ),
          associatedQuestionnaires: null
        }
      ]
    }
  ]
}

function toThirdBlock(insured: HealthHiringInsured) {
  const question1 = {
    questionCode: '1101',
    dataType: 'SUBCUEST',
    responses: [
      {
        value: null,
        responseText: '',
        associatedQuestionnaires: [
          {
            questionCode: '0102',
            dataType: 'RADIO',
            responses: [
              {
                value: responseYesNo(!insured.healthSituation.cardiac.isEmpty()),
                responseText: insured.healthSituation.cardiac.description,
                associatedQuestionnaires: null
              }
            ]
          },
          {
            questionCode: '0202',
            dataType: 'RADIO',
            responses: [
              {
                value: responseYesNo(!insured.healthSituation.metabolic.isEmpty()),
                responseText: insured.healthSituation.metabolic.description,
                associatedQuestionnaires: null
              }
            ]
          },
          {
            questionCode: '0302',
            dataType: 'RADIO',
            responses: [
              {
                value: responseYesNo(!insured.healthSituation.rheumatic.isEmpty()),
                responseText: insured.healthSituation.rheumatic.description,
                associatedQuestionnaires: null
              }
            ]
          },
          {
            questionCode: '0402',
            dataType: 'RADIO',
            responses: [
              {
                value: responseYesNo(!insured.healthSituation.nervous.isEmpty()),
                responseText: insured.healthSituation.nervous.description,
                associatedQuestionnaires: null
              }
            ]
          },
          {
            questionCode: '0502',
            dataType: 'RADIO',
            responses: [
              {
                value: responseYesNo(!insured.healthSituation.hematologic.isEmpty()),
                responseText: insured.healthSituation.hematologic.description,
                associatedQuestionnaires: null
              }
            ]
          },
          {
            questionCode: '0602',
            dataType: 'RADIO',
            responses: [
              {
                value: responseYesNo(!insured.healthSituation.urogenital.isEmpty()),
                responseText: insured.healthSituation.urogenital.description,
                associatedQuestionnaires: null
              }
            ]
          },
          {
            questionCode: '0702',
            dataType: 'RADIO',
            responses: [
              {
                value: responseYesNo(!insured.healthSituation.psychiatric.isEmpty()),
                responseText: insured.healthSituation.psychiatric.description,
                associatedQuestionnaires: null
              }
            ]
          },
          {
            questionCode: '0802',
            dataType: 'RADIO',
            responses: [
              {
                value: responseYesNo(!insured.healthSituation.infectious.isEmpty()),
                responseText: insured.healthSituation.infectious.description,
                associatedQuestionnaires: null
              }
            ]
          }
        ]
      }
    ]
  }

  const question2 = {
    questionCode: '0501',
    dataType: 'RADIO',
    responses: [
      {
        value: responseYesNo(!insured.healthSituation.medication.isEmpty()),
        responseText: insured.healthSituation.medication.description,
        associatedQuestionnaires: null
      }
    ]
  }

  const responsesQuestionnaires3 = []
  if (insured.healthSituation.alcohol.type) {
    responsesQuestionnaires3.push({
      value: 'a',
      responseText: '',
      associatedQuestionnaires: [
        {
          questionCode: '2001A1',
          dataType: 'RADIO',
          responses: [
            {
              value: insured.healthSituation.alcohol.type,
              responseText: '',
              associatedQuestionnaires: null
            }
          ]
        },
        {
          questionCode: '2001A2',
          dataType: 'RADIO',
          responses: [
            {
              value: insured.healthSituation.alcohol.frequency,
              responseText: '',
              associatedQuestionnaires: null
            }
          ]
        }
      ]
    })
  }

  if (insured.healthSituation.smoking.type) {
    responsesQuestionnaires3.push({
      value: 'f',
      responseText: '',
      associatedQuestionnaires: [
        {
          questionCode: '1001A1',
          dataType: 'RADIO',
          responses: [
            {
              value: insured.healthSituation.smoking.type,
              responseText: '',
              associatedQuestionnaires: null
            }
          ]
        },
        {
          questionCode: '1001A2',
          dataType: 'RADIO',
          responses: [
            {
              value: insured.healthSituation.smoking.frequency,
              responseText: '',
              associatedQuestionnaires: null
            }
          ]
        }
      ]
    })
  }

  if (insured.healthSituation.drugs.description) {
    responsesQuestionnaires3.push({
      value: 'd',
      responseText: insured.healthSituation.drugs.description,
      associatedQuestionnaires: null
    })
  }

  const associatedQuestionnaires3 = [
    {
      questionCode: '1801',
      dataType: 'CHECK',
      responses: responsesQuestionnaires3
    }
  ]
  const isFilled =
    !insured.healthSituation.alcohol.isEmpty() ||
    !insured.healthSituation.smoking.isEmpty() ||
    !insured.healthSituation.drugs.isEmpty()

  const question3 = {
    questionCode: '0901',
    dataType: 'RADIO',
    responses: [
      {
        value: responseYesNo(isFilled),
        responseText: '',
        associatedQuestionnaires: isFilled ? associatedQuestionnaires3 : null
      }
    ]
  }

  const question4 = {
    questionCode: '1201',
    dataType: 'RADIO',
    responses: [
      {
        value: responseYesNo(!insured.healthSituation.injuries.isEmpty()),
        responseText: insured.healthSituation.injuries.description,
        associatedQuestionnaires: null
      }
    ]
  }

  const question5 = {
    questionCode: '1601',
    dataType: 'RADIO',
    responses: [
      {
        value: responseYesNo(!insured.healthSituation.disability.isEmpty()),
        responseText: insured.healthSituation.disability.description,
        associatedQuestionnaires: null
      }
    ]
  }

  return [question1, question2, question3, question4, question5]
}

function fromFirstBlock(questions: any[]) {
  const weight = questions[0].responses[0].value
  const height = questions[1].responses[0].value

  return { weight, height }
}

function fromSecondBlock(questions: any[]) {
  const filled = !!questions[0].responses[0]?.value

  const medical = HealthQuestionnaireMapper.parseAdmissions(
    questions[0].responses[0]?.responseText ?? ''
  )
  const surgical = HealthQuestionnaireMapper.parseAdmissions(
    questions[1].responses[0]?.responseText ?? ''
  )
  const cancer = HealthQuestionnaireMapper.parseAdmissions(
    questions[2].responses[0]?.responseText ?? ''
  )
  const symptoms = HealthQuestionnaireMapper.parseAdmissions(
    questions[3].responses[0]?.responseText ?? ''
  )

  return new HealthHiringInsuredMedicalSituation({
    medical,
    surgical,
    cancer,
    symptoms,
    filled
  })
}

function fromThirdBlock(questions: any[]) {
  const firstQuestion = questions[0].responses[0]
  const filled = !!firstQuestion.associatedQuestionnaires[0].responses[0].value

  const cardiac = new HealthSituationDescription(
    firstQuestion.associatedQuestionnaires[0].responses[0].responseText
  )
  const metabolic = new HealthSituationDescription(
    firstQuestion.associatedQuestionnaires[1].responses[0].responseText
  )
  const rheumatic = new HealthSituationDescription(
    firstQuestion.associatedQuestionnaires[2].responses[0].responseText
  )
  const nervous = new HealthSituationDescription(
    firstQuestion.associatedQuestionnaires[3].responses[0].responseText
  )
  const hematologic = new HealthSituationDescription(
    firstQuestion.associatedQuestionnaires[4].responses[0].responseText
  )
  const urogenital = new HealthSituationDescription(
    firstQuestion.associatedQuestionnaires[5].responses[0].responseText
  )
  const psychiatric = new HealthSituationDescription(
    firstQuestion.associatedQuestionnaires[6].responses[0].responseText
  )
  const infectious = new HealthSituationDescription(
    firstQuestion.associatedQuestionnaires[7].responses[0].responseText
  )

  const secondQuestion = questions[1].responses[0]
  const medication = new HealthSituationDescription(secondQuestion.responseText)

  const thirdQuestion = questions[2].responses[0].associatedQuestionnaires[0]
  const alcoholQuestion = thirdQuestion?.responses.find((r: any) => r.value === 'a')
  const smokingQuestion = thirdQuestion?.responses.find((r: any) => r.value === 'f')
  const drugsQuestion = thirdQuestion?.responses.find((r: any) => r.value === 'd')

  const alcohol = new HealthSituationUsage(
    alcoholQuestion ? alcoholQuestion?.associatedQuestionnaires[0]?.responses[0]?.value : '',
    alcoholQuestion ? alcoholQuestion?.associatedQuestionnaires[1]?.responses[0]?.value : ''
  )
  const smoking = new HealthSituationUsage(
    smokingQuestion ? smokingQuestion?.associatedQuestionnaires[0]?.responses[0]?.value : '',
    thirdQuestion ? smokingQuestion?.associatedQuestionnaires[1]?.responses[0]?.value : ''
  )
  const drugs = new HealthSituationDescription(drugsQuestion ? drugsQuestion?.responseText : '')

  const injuries = new HealthSituationDescription(questions[3].responses[0].responseText)
  const disability = new HealthSituationDescription(questions[4].responses[0].responseText)

  return new HealthHiringInsuredHealthSituation({
    cardiac,
    metabolic,
    rheumatic,
    nervous,
    hematologic,
    urogenital,
    psychiatric,
    infectious,
    medication,
    alcohol,
    smoking,
    drugs,
    injuries,
    disability,
    filled
  })
}

export default class HealthQuestionnaireMapper {
  static stringlifyAdmissions(admissions: MedicalSituationAdmission[]): string {
    return admissions.reduce((acc, admission, i) => {
      return acc + `__$${i + 1}_[${admission.month}/${admission.year}]_${admission.reason}`
    }, '')
  }

  static parseAdmissions(admissions: string): MedicalSituationAdmission[] {
    const regex = /__\$\d+_\[(\d{2}\/\d{4})\]_(.*?)(?=__\$|$)/g

    const matches = []
    let match

    while ((match = regex.exec(admissions ?? '')) !== null) {
      const date = match[1]
      const month = String(Number(date?.match(/\d{2}/g)![0]))
      const year = String(Number(date?.match(/\d{4}/g)![0]))
      const reason = match[2].trim()

      matches.push(new MedicalSituationAdmission({ month, year, reason }))
    }

    return matches
  }

  static toInsuredQuestionnaire(insured: HealthHiringInsured) {
    return [
      {
        questionnaireType: 'DETA',
        language: 'ESPA',
        questions: toFirstBlock(insured)
      },
      {
        questionnaireType: 'DETA',
        language: 'ESPA',
        questions: [...toSecondBlock(insured), ...toThirdBlock(insured)]
      }
    ]
  }

  static fromQuestionnaireResponse(questionnaire: any[]) {
    if (!questionnaire || questionnaire?.length === 0) {
      return {
        weight: null,
        height: null,
        medicalSituation: new HealthHiringInsuredMedicalSituation(),
        healthSituation: new HealthHiringInsuredHealthSituation()
      }
    }

    const questions = questionnaire[0].questions

    const measuresQuestions = questions.filter((q: any) => ['05', '06'].includes(q.questionCode))
    const { weight, height } = fromFirstBlock(measuresQuestions)
    // const { weight, height } = fromFirstBlock(questions.slice(0, 2))

    const medicalQuestions = questions.filter((q: any) =>
      ['0101', '0301', '1401', '0701'].includes(q.questionCode)
    )
    const medicalSituation = fromSecondBlock(medicalQuestions)

    // const medicalSituation = fromSecondBlock(questions.slice(2, 6))

    const healthQuestions = questions.filter((q: any) =>
      ['1101', '0501', '0901', '1201', '1601'].includes(q.questionCode)
    )
    const healthSituation = fromThirdBlock(healthQuestions)
    // const healthSituation = fromThirdBlock(questions.slice(6))

    return {
      weight,
      height,
      medicalSituation,
      healthSituation
    }
  }
}
